import React, { useMemo } from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const CategoryCard = ({
  categoryKey,
  categoryLabel,
  categoryHex,
  CategoryIcon,
  sources,
  status: STATUS,
  isSelected,
  onClick,
  loading,
}) => {
  const hasSources = sources.length > 0;

  // Count statuses for dot display
  const statusCounts = useMemo(() => {
    const counts = { red: 0, yellow: 0, green: 0 };
    sources.forEach((s) => {
      if (counts[s.status] !== undefined) counts[s.status]++;
    });
    return counts;
  }, [sources]);

  // Derive alert text lines
  const alerts = useMemo(() => {
    const lines = [];

    // Total missing days across all sources
    const totalMissingDays = sources.reduce(
      (sum, s) => sum + (s.missingDays?.length ?? 0),
      0
    );
    if (totalMissingDays > 0) {
      lines.push(`${totalMissingDays} missing day${totalMissingDays !== 1 ? 's' : ''}`);
    }

    // Count sources with missing stores
    const sourcesWithMissingStores = sources.filter(
      (s) => s.totalStores > 0 && s.storesPresent < s.totalStores
    ).length;
    if (sourcesWithMissingStores > 0) {
      lines.push(`${sourcesWithMissingStores} source${sourcesWithMissingStores !== 1 ? 's' : ''} missing stores`);
    }

    return lines;
  }, [sources]);

  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        flex: '1 1 0',
        minWidth: 180,
        minHeight: 120,
        cursor: 'pointer',
        borderRadius: 2,
        border: isSelected ? '2px solid' : '1px solid',
        borderColor: isSelected ? categoryHex : 'divider',
        borderTop: `4px solid ${categoryHex}`,
        backgroundColor: isSelected ? alpha(categoryHex, 0.07) : 'white',
        boxShadow: isSelected
          ? `0 2px 8px ${alpha(categoryHex, 0.18)}`
          : 'none',
        px: 2,
        py: 1.5,
        transition: 'all 0.15s ease',
        '&:hover': {
          backgroundColor: alpha(categoryHex, 0.06),
        },
      }}
    >
      {/* Icon + Label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CategoryIcon sx={{ color: categoryHex, fontSize: 20 }} />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, fontFamily: 'Lato' }}
        >
          {categoryLabel}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} sx={{ color: categoryHex }} />
        </Box>
      ) : (
        <>
          {/* Source count */}
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
          >
            {sources.length} source{sources.length !== 1 ? 's' : ''}
          </Typography>

          {hasSources ? (
            <>
              {/* Status dot counts */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                {statusCounts.red > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <FiberManualRecordIcon
                      sx={{ fontSize: 8, color: STATUS.red.color }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: STATUS.red.color, fontWeight: 600 }}
                    >
                      {statusCounts.red}
                    </Typography>
                  </Box>
                )}
                {statusCounts.yellow > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <FiberManualRecordIcon
                      sx={{ fontSize: 8, color: STATUS.yellow.color }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: STATUS.yellow.color, fontWeight: 600 }}
                    >
                      {statusCounts.yellow}
                    </Typography>
                  </Box>
                )}
                {statusCounts.green > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <FiberManualRecordIcon
                      sx={{ fontSize: 8, color: STATUS.green.color }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: STATUS.green.color, fontWeight: 600 }}
                    >
                      {statusCounts.green}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Alert text */}
              {alerts.length > 0 && (
                <Box>
                  {alerts.map((text, i) => (
                    <Typography
                      key={i}
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        lineHeight: 1.4,
                      }}
                    >
                      {text}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <Typography
              variant="caption"
              sx={{ color: 'text.disabled', fontStyle: 'italic' }}
            >
              Not configured
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
};

export default CategoryCard;
